import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import { customStyles } from "../../assets/table-style";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../assets/global/url";
import { Link, useNavigate } from "react-router-dom";
import { ThreeDot } from "react-loading-indicators";
import NoAccess from "../../assets/img/NoAccess.png";
import { format } from "date-fns";
import { useServerPagination } from "../../hooks/customHook/paginationHook/useServerPagination";
import { PaginationControls } from "../../hooks/customHook/paginationHook/usePagination";
import DateRangePicker from "../../components/DateRangePicker";
import { getMonthBoundaries, initializeCutoff } from "../../utils/newdate";
import FilterStore from "../../hooks/customHook/FilterStore";
import { useServerSort } from "../../hooks/customHook/sortingHook/useTableSort";

const InventoryCounting = ({ authrztn }) => {
  const navigate = useNavigate();
  const pageRestored = useRef(false);
  const isFirstFilterReset = useRef(true);
  const isClearingFilters = useRef(false);

  const [inboundData, setInboundData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [searchText, setSearchText] = useState(() =>
    FilterStore.ICCAgetSearch(),
  );

  useEffect(() => {
    FilterStore.ICCAsaveSearch(searchText);
  }, [searchText]);

  const [filterColumn, setFilterColumn] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCutOff, setSelectedCutOff] = useState("");
  const [cutOffDate, setCutOffDate] = useState("");

  const fetchCutoff = async () => {
    const savedFilter = FilterStore.get();

    const initialCutoff =
      savedFilter ??
      (() => {
        const boundaries = getMonthBoundaries();
        return initializeCutoff(boundaries);
      })();

    setSelectedCutOff(initialCutoff);
    setCutOffDate(initialCutoff);
    reloadTable(initialCutoff, searchText, filterColumn, statusFilter);
  };

  useEffect(() => {
    fetchCutoff();
  }, []);

  const pagination = useServerPagination(
    BASE_URL + "/inventoryCounting/getInventoryCounting",
    FilterStore.ICCAgetPerPage(),
  );

  // No initial sortBy: the backend default (newest first) has no visible column,
  // so every header starts neutral until the user picks one
  const sort = useServerSort(pagination);

  const reloadTable = (
    cutOff,
    search = searchText,
    col = filterColumn,
    stat = statusFilter,
  ) => {
    pagination.updateParams({
      startDate: cutOff?.from || "",
      endDate: cutOff?.to || "",
      searchText: search,
      filterColumn: col,
      status: stat,
    });
    setIsLoading(false);
  };

  useEffect(() => {
    setInboundData(pagination.data);
    setFilteredData(pagination.data);
  }, [pagination.data]);

  // Clear Filters functionality
  const handleClearFilters = () => {
    isClearingFilters.current = true;

    const emptySearch = "";
    const emptyCol = "all";
    const emptyStatus = "all";

    // Calculate defaults for visual UI reset
    const boundaries = getMonthBoundaries();
    const defaultCutoff = initializeCutoff(boundaries);

    // 1. Reset UI Display States to system defaults
    setSearchText(emptySearch);
    setFilterColumn(emptyCol);
    setStatusFilter(emptyStatus);
    setSelectedCutOff(defaultCutoff);
    setCutOffDate(defaultCutoff);

    // 2. Clear persisting cache storages
    FilterStore.ICCAsaveSearch(emptySearch);
    FilterStore.save("", "");
    FilterStore.ICCAsavePage(1);

    // 3. Reset hook page tracking
    pagination.setCurrentPage(1);

    // 4. Force backend request to skip dates completely by passing empty strings
    pagination.updateParams({
      startDate: "",
      endDate: "",
      searchText: emptySearch,
      filterColumn: emptyCol,
      status: emptyStatus,
    });

    setTimeout(() => {
      isClearingFilters.current = false;
    }, 100);
  };

  const columns = [
    {
      name: "Transaction Numbers",
      selector: (row) => row.transaction_id,
      sortField: "transaction_id",
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks || "--",
      sortField: "remarks",
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortField: "status",
      cell: (row) => {
        let color;
        switch (row.status) {
          case "For Approval":
            color = "#FFA500";
            break;
          case "Approved":
            color = "#3B9F3F";
            break;
          case "Rejected":
            color = "#FF0000";
            break;
          default:
            color = "initial";
        }
        return (
          <div
            style={{
              padding: "5px 10px",
              borderRadius: "5px",
              color: color,
              textTransform: "uppercase",
              fontWeight: "bold",
            }}>
            {row.status}
          </div>
        );
      },
    },
    {
      name: "Counting Time",
      selector: (row) => format(row.countingDate, "MMM/dd/yyyy"),
      sortField: "counting_date",
    },
    {
      name: "User",
      selector: (row) => row.user,
      sortField: "user",
    },
  ];

  const userData = inboundData?.map((data, i) => {
    return {
      key: i,
      inventoryCountingId: data.inventory_counting_id,
      transaction_id: data.transaction_id,
      countingDate: data.counting_date,
      remarks: data.remarks,
      status: data.status,
      user: data.user,
    };
  });

  const handleUpdateModalToggle = (row) => {
    navigate(`/inventory/inventory-counting-update/${row.inventoryCountingId}`);
  };

  const handleDateRangeChange = (startDate, endDate) => {
    if (isClearingFilters.current) return;
    const cutoff = {
      id: "custom",
      from: startDate,
      to: endDate,
    };
    setSelectedCutOff(cutoff);
    setCutOffDate(cutoff);
    FilterStore.save(startDate, endDate);
    pagination.setCurrentPage(1);
    FilterStore.ICCAsavePage(1);
    reloadTable(cutoff, searchText, filterColumn, statusFilter);
  };

  useEffect(() => {
    if (!pageRestored.current) return;
    if (pagination.currentPage) {
      FilterStore.ICCAsavePage(pagination.currentPage);
    }
  }, [pagination.currentPage]);

  useEffect(() => {
    if (pagination.itemsPerPage) {
      FilterStore.ICCAsavePerPage(pagination.itemsPerPage);
    }
  }, [pagination.itemsPerPage]);

  useEffect(() => {
    const savedPage = FilterStore.ICCAgetPage();
    if (savedPage > 1) {
      setTimeout(() => {
        pagination.setCurrentPage(savedPage);
        pageRestored.current = true;
      }, 0);
    } else {
      setTimeout(() => {
        pageRestored.current = true;
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (isFirstFilterReset.current) {
      isFirstFilterReset.current = false;
      return;
    }
    if (isClearingFilters.current) return;

    pagination.setCurrentPage(1);
    FilterStore.ICCAsavePage(1);
    reloadTable(cutOffDate, searchText, filterColumn, statusFilter);
  }, [searchText, filterColumn, statusFilter]);

  return (
    <div className="h-100 w-100 border bg-white custom-container">
      {isLoading ? (
        <div className="loading-container">
          <ThreeDot
            variant="brick-stack"
            color="#6290FE"
            size="large"
            text="Loading Data..."
            textColor=""
          />
        </div>
      ) : authrztn.includes("InventoryCounting-View") ? (
        <>
          <div className="w-100 p-2 d-flex flex-row justify-content-between">
            <div className="d-flex flex-column title-custom">
              <span className="fs-3">INVENTORY COUNTING</span>
              <span>STOCK COUNTING</span>
            </div>
            {authrztn.includes("InventoryCounting-Add") && (
              <div>
                <Link
                  to="/inventory/inventory-counting-create"
                  className="btn btn-primary d-flex flex-row align-items-center title-button">
                  <i className="bx bx-plus fs-5"></i> Create
                </Link>
              </div>
            )}
          </div>
          <div className="container-fluid mt-4">
            <div className="row g-2">
              <div className="col-sm-auto">
                <DateRangePicker
                  startDate={
                    cutOffDate?.from ? new Date(cutOffDate.from) : null
                  }
                  endDate={cutOffDate?.to ? new Date(cutOffDate.to) : null}
                  onDateRangeChange={handleDateRangeChange}
                  label="Date Range"
                />
              </div>
              <div className="col-sm-auto">
                <label htmlFor="statusFilter" className="form-label">
                  Status
                </label>
                <select
                  id="statusFilter"
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="all">All</option>
                  <option value="For Approval">For Approval</option>
                  <option value="Approved">Approved</option>
                  <option value="Rejected">Rejected</option>
                </select>
              </div>
              <div className="col-sm-auto d-flex align-items-end">
                <button
                  type="button"
                  className="btn btn-secondary w-100"
                  onClick={handleClearFilters}>
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
          <div className="w-100 mt-3 mb-2 container-fluid">
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline-secondary dropdown-toggle-split"
                data-bs-toggle="dropdown"
                aria-expanded="false">
                <i className="fa-solid fa-sliders"></i>
              </button>
              <ul className="dropdown-menu dropdown-menu-end">
                <li>
                  <button
                    className={`dropdown-item ${
                      filterColumn === "all" ? "active" : ""
                    }`}
                    onClick={() => setFilterColumn("all")}>
                    All
                  </button>
                </li>
                {[
                  { value: "remarks", label: "Remarks" },
                  { value: "transaction_id", label: "Transaction Number" },
                  { value: "status", label: "Status" },
                  { value: "counting_date", label: "Counting Time" },
                  { value: "user", label: "User" },
                ].map(({ value, label }) => (
                  <li key={value}>
                    <button
                      className={`dropdown-item ${
                        filterColumn === value ? "active" : ""
                      }`}
                      onClick={() => setFilterColumn(value)}>
                      {label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          {/* data table */}
          <div className="w-100 mt-3 container-fluid">
            <DataTable
              columns={sort.sortColumns(columns)}
              data={userData}
              customStyles={customStyles}
              className="dataTable"
              onRowClicked={handleUpdateModalToggle}
              {...sort.sortProps}
            />
            <PaginationControls {...pagination} />
          </div>
        </>
      ) : (
        <div className="no-access">
          <img src={NoAccess} alt="NoAccess" className="no-access-img" />
          <h3>You don't have access to this function.</h3>
        </div>
      )}
    </div>
  );
};

export default InventoryCounting;
